import {
  getWikiControllerGetWikiStatusQueryKey,
  type GetWikiDocByWorkspaceArrayResponseOutput,
  type GetWikiDocByWorkspaceResponseOutput,
  type PostDocsStatusBody,
  type PostDocsStatusBodyAction,
  type SuccessResponseOutput,
  useWikiControllerGetWikiDocByWorkspace,
  type wikiControllerGetWikiDocByWorkspace,
  type WikiControllerGetWikiDocByWorkspaceParams,
  type wikiControllerGetWikiDocsByWorkspace,
} from '@docnboard/rest';
import {
  type MutateOptions,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useLiveData, useService } from '@toeverything/infra';
import { isEmpty } from 'es-toolkit/compat';
import { useCallback, useMemo } from 'react';

import { ServerConfigService } from '../../cloud';
import {
  getWikiQueryKey,
  useGetWikiDocsByWorkspace,
  useGetWikiStatus,
  usePostActionDocs,
} from '../api/query';

type PostActionDocsOptions = MutateOptions<
  SuccessResponseOutput,
  SuccessResponseOutput,
  { data: PostDocsStatusBody },
  unknown
>;

export const useActionDocsWiki = (workspaceId: string) => {
  const _queryClient = useQueryClient();

  // TODO можно подумать над использованием useIsMutating,
  // чтобы отображать лоадер во всех местах

  const postActionDocs = usePostActionDocs({
    mutation: {
      onSuccess: async () => {
        await _queryClient.invalidateQueries({
          queryKey: getWikiQueryKey(workspaceId),
        });
      },
    },
  });

  const onActionDocs = useCallback(
    (
      docId: string | string[],
      actionType: PostDocsStatusBodyAction,
      options?: PostActionDocsOptions
    ) => {
      if (isEmpty(docId)) return;

      postActionDocs.mutate(
        {
          data: {
            workspaceId,
            docIds: Array.isArray(docId) ? docId : [docId],
            action: actionType,
          },
        },
        options
      );
    },
    [postActionDocs, workspaceId]
  );

  return { onActionDocs, postActionDocs, isPending: postActionDocs.isPending };
};

type QueryOptions<
  TData = Awaited<ReturnType<typeof wikiControllerGetWikiDocsByWorkspace>>,
  TError = GetWikiDocByWorkspaceArrayResponseOutput,
> = UseQueryOptions<
  Awaited<ReturnType<typeof wikiControllerGetWikiDocsByWorkspace>>,
  TError,
  TData
>;

export const useGetDocsWiki = (workspaceId: string, query?: QueryOptions) => {
  const wikiStatus = useWikiStatus({ workspaceId });

  const wikiDocsRes = useGetWikiDocsByWorkspace(
    { workspaceId },
    {
      query: {
        ...query,
        enabled: !!wikiStatus.data?.data?.enable && (query?.enabled ?? true),
        queryKey: getWikiQueryKey(workspaceId),
      },
    }
  );

  const wikiDocsMap = useMemo(() => {
    const items = wikiDocsRes.data?.data?.items ?? [];
    return new Map(items.map(doc => [doc.docId, doc]));
  }, [wikiDocsRes.data]);

  const getWikiDocById = useCallback(
    (docId: string) => {
      return wikiDocsMap.get(docId);
    },
    [wikiDocsMap]
  );

  return { wikiDocsMap, wikiDocsRes, getWikiDocById };
};

export const useWikiStatus = (params: { workspaceId: string }) => {
  const { workspaceId } = params;
  const serverConfig = useService(ServerConfigService).serverConfig;
  const wikiEnabled = useLiveData(serverConfig.features$.map(f => f?.wiki));

  return useGetWikiStatus(
    { workspaceId },
    {
      query: {
        enabled: !!wikiEnabled,
        staleTime: Infinity,
        queryKey: getWikiControllerGetWikiStatusQueryKey({ workspaceId }),
      },
    }
  );
};

type GetWikiDocOptions<
  TData = Awaited<ReturnType<typeof wikiControllerGetWikiDocByWorkspace>>,
  TError = GetWikiDocByWorkspaceResponseOutput,
> = {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof wikiControllerGetWikiDocByWorkspace>>,
    TError,
    TData
  >;
};

// хук с настройкой retry для 404 ошибок, возможно вынести на глобальный уровень
export const useGetWikiDocByWorkspace = (
  docId: string,
  params: WikiControllerGetWikiDocByWorkspaceParams,
  options?: GetWikiDocOptions
) => {
  const wikiStatus = useWikiStatus({ workspaceId: params.workspaceId });
  const serverConfig = useService(ServerConfigService).serverConfig;
  const wikiEnabled = useLiveData(serverConfig.features$.map(f => f?.wiki));

  return useWikiControllerGetWikiDocByWorkspace(docId, params, {
    query: {
      retry: (failureCount, error) => {
        // Не повторяем запросы при 404 ошибках
        if (error.error?.status === 404) return false;

        // Для остальных ошибок используем стандартную логику (3 попытки)
        return failureCount < 3;
      },
      enabled:
        !!wikiEnabled &&
        !!wikiStatus.data?.data?.enable &&
        (options?.query?.enabled ?? true),
      queryKey: getWikiQueryKey(params.workspaceId, docId),
      ...options?.query,
    },
    ...options,
  });
};
